import { IButtonProps } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useSubscription } from "@/hooks/useSubscription";

import { PLAN_NAMES } from "@/constants/CPlans";

import {
	PLAN_LIMIT_EVENT,
	TPlanCode,
	TPlanGateKey,
	TPlanLimitDetails,
	TPlanGate,
} from "@/types/TSubscription";

/** Ask the upgrade modal to open on these details, as a 402 would. */
export const openUpgrade = (details: TPlanLimitDetails) => {
	window.dispatchEvent(
		new CustomEvent<TPlanLimitDetails>(PLAN_LIMIT_EVENT, {
			detail: details,
		})
	);
};

/** The hover hint of a locked button. */
export const requiredPlanHint = (requiredPlan: TPlanCode | null): string =>
	requiredPlan
		? t("subscription.gate.required", { plan: PLAN_NAMES[requiredPlan] })
		: t("subscription.gate.unavailable");

/**
 * Gating for one button: `locked` and the plan that unlocks it, plus
 * `button(props)` which turns any button props into the locked version
 * — lock picto, hint, click opens the upgrade modal instead of acting.
 * UX only: the API refuses regardless.
 */
export const usePlanGate = (key: TPlanGateKey) => {
	const { gate, plan } = useSubscription();
	const state: TPlanGate = gate(key);

	const details = (): TPlanLimitDetails => ({
		limit: key,
		plan: plan?.code ?? "free",
		max: state.usage?.max ?? 0,
		used: state.usage?.used ?? 0,
		requiredPlan: state.requiredPlan,
	});

	const open = () => openUpgrade(details());

	const button = <P extends IButtonProps>(props: P): P =>
		state.locked
			? {
					...props,
					picto: "lock",
					title: requiredPlanHint(state.requiredPlan),
					disabled: false,
					onClick: open,
				}
			: props;

	return { ...state, open, button };
};
